import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-84705c5e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { style: {"width":"100%","height":"100%"} }
const _hoisted_2 = ["transform"]
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("g", { class: "program-inner-back" }, null, -1))
const _hoisted_4 = {
  key: 1,
  class: "running"
}
const _hoisted_5 = { class: "buttons" }
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("img", { src: "/logo.svg" }, null, -1))
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { style: {"flex":"1"} }, null, -1))
const _hoisted_8 = /*#__PURE__*/_createTextVNode(" v0.7.0 ")

import CursorMode, {
  CursorMode as CursorModeType,
} from "@/components/CursorMode.vue";


import {
  InjectionKey,
  provide,
  Ref,
  ref,
  computed,
  toRefs,
  watch,
  DeepReadonly,
} from "vue";
import { useStore } from "../store";
import DraggingSnake from "@/components/DraggingSnake.vue";
import { Vector2 } from "@/utils/block";
import ScrollBar from "@/components/ScrollBar.vue";
import VScrollBar from "@/components/VScrollBar.vue";
import { Snake } from "@/utils/snake";
import SnakeVue from "@/components/Snake.vue";
import ShareTwitter from "@/components/ShareTwitter.vue";
import ShowVDNA from "@/components/ShowVDNA.vue";
import ChangeLocale from "@/components/ChangeLocale.vue";

export const getFixedPositionKey: InjectionKey<
  (absolutePos: DeepReadonly<Vector2>) => Vector2
> = Symbol("getFixedPosition");
export const getAbsolutePositionKey: InjectionKey<
  (fixedPos: DeepReadonly<Vector2>) => Vector2
> = Symbol("getAbsolutePosition");
export const willBeDeletedKey: InjectionKey<(fixedPos: Vector2) => boolean> =
  Symbol("wellBeDeleted");

export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const { snakes, draggingSnake: _draggingSnake, isRunning } = toRefs(useStore());
const draggingSnake = ref<Snake | null>(null);
watch(_draggingSnake, () => {
  if (_draggingSnake.value !== null) {
    draggingSnake.value = Snake.copy(_draggingSnake.value);
  } else {
    draggingSnake.value = null;
  }
});

const filteredSnakes = computed(() =>
  Object.values(snakes.value).filter((snake) => snake.visible)
);

const programRef: Ref<HTMLElement | null> = ref(null);

const programWidth = ref(0);
const programHeight = ref(0);

watch(programRef, (_, prevProgramRef) => {
  if (!prevProgramRef && programRef.value) {
    ro.observe(programRef.value);
  }
  if (programRef.value) {
    programWidth.value = programRef.value.clientWidth;
    programHeight.value = programRef.value.clientHeight;
  }
});

const ro = new ResizeObserver(() => {
  if (programRef.value) {
    programWidth.value = programRef.value.clientWidth;
    programHeight.value = programRef.value.clientHeight;
  }
});

const scrollX = ref(0);
const scrollY = ref(0);

const limits = computed(() => {
  return [
    ...Object.values(snakes.value).flatMap<
      DeepReadonly<{
        tail: Vector2;
        head: Vector2;
        height: number;
        isDragging: boolean;
      }>
    >((snake) =>
      snake.visible
        ? {
            tail: snake.anchorTail,
            head: snake.anchorNext,
            height: snake.height,
            isDragging: false,
          }
        : []
    ),
    ...(draggingSnake.value
      ? [
          {
            tail: draggingSnake.value.anchorTail,
            head: draggingSnake.value.anchorNext,
            height: draggingSnake.value.height,
            isDragging: true,
          },
        ]
      : []),
  ].reduce(
    (a, b) => [
      [
        Math.min(a[0][0], b.tail[0] - 20),
        b.isDragging ? a[0][1] : Math.min(a[0][1], b.tail[1] - b.height - 20),
      ],
      [
        Math.max(a[1][0], b.head[0] + 20),
        b.isDragging ? a[1][1] : Math.max(a[1][1], b.tail[1] + 20),
      ],
    ],
    [
      [Math.min(0, scrollX.value), Math.min(0, scrollY.value)],
      [
        Math.max(0, scrollX.value) + programWidth.value,
        Math.max(0, scrollY.value) + programHeight.value,
      ],
    ]
  );
});

function normalizeScrollX(newScrollX: number) {
  return Math.min(
    Math.max(newScrollX, limits.value[0][0]),
    limits.value[1][0] - programWidth.value
  );
}

function normalizeScrollY(newScrollY: number) {
  return Math.min(
    Math.max(newScrollY, limits.value[0][1]),
    limits.value[1][1] - programHeight.value
  );
}

watch(limits, () => {
  scrollX.value = normalizeScrollX(scrollX.value);
  scrollY.value = normalizeScrollY(scrollY.value);
});

const scrollRate = 0.1;
let requestAnimationFrameId: number | null = null;
function checkBorder() {
  requestAnimationFrameId = null;
  if (!draggingSnake.value || !draggingSnake.value.grabbingBlockUUID) return;
  const blockBoundary = draggingSnake.value.getBlockBoundary(
    draggingSnake.value.grabbingBlockUUID
  );
  if (!blockBoundary) return;
  const positiveX =
    blockBoundary.headX - (scrollX.value + programWidth.value + 5);
  const negativeX = scrollX.value - 5 - blockBoundary.tailX;
  if (positiveX > 0) {
    scrollX.value += positiveX * scrollRate;
    draggingSnake.value.anchorTail[0] += positiveX * scrollRate;
  } else if (negativeX > 0) {
    scrollX.value -= negativeX * scrollRate;
    draggingSnake.value.anchorTail[0] -= negativeX * scrollRate;
  }
  requestAnimationFrameId = requestAnimationFrame(checkBorder);
}
watch(draggingSnake, () => {
  if (requestAnimationFrameId === null && draggingSnake.value !== null) {
    checkBorder();
  }
});

const cursorMode = ref<CursorModeType>("move");

function getFixedPosition(absolutePos: Vector2) {
  if (programRef.value === null) {
    throw new Error("programRef is null.");
  }
  const boundingRect = programRef.value.getBoundingClientRect();
  return [
    absolutePos[0] + boundingRect.x - scrollX.value,
    absolutePos[1] + boundingRect.y - scrollY.value,
  ];
}
provide(getFixedPositionKey, getFixedPosition);

function getAbsolutePosition(fixedPos: DeepReadonly<Vector2>) {
  if (programRef.value === null) {
    throw new Error("programRef is null.");
  }
  const boundingRect = programRef.value.getBoundingClientRect();
  return [
    fixedPos[0] - boundingRect.x + scrollX.value,
    fixedPos[1] - boundingRect.y + scrollY.value,
  ];
}
provide(getAbsolutePositionKey, getAbsolutePosition);

provide(willBeDeletedKey, (fixedPos: DeepReadonly<Vector2>) => {
  if (programRef.value === null) {
    throw new Error("programRef is null.");
  }
  const boundingRect = programRef.value.getBoundingClientRect();
  return fixedPos[1] >= boundingRect.y + boundingRect.height;
});

function wheel(e: WheelEvent) {
  e.preventDefault();
  scrollX.value = normalizeScrollX(scrollX.value + e.deltaX);
  scrollY.value = normalizeScrollY(scrollY.value + e.deltaY);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "program",
    ref_key: "programRef",
    ref: programRef,
    onWheel: wheel
  }, [
    (_openBlock(), _createElementBlock("svg", _hoisted_1, [
      _createElementVNode("g", {
        class: "program-inner",
        transform: `translate(${-scrollX.value}, ${-scrollY.value})`
      }, [
        _hoisted_3,
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredSnakes), (snake) => {
          return (_openBlock(), _createBlock(SnakeVue, {
            key: snake.uuid,
            snake: snake,
            "cursor-mode": cursorMode.value
          }, null, 8, ["snake", "cursor-mode"]))
        }), 128))
      ], 8, _hoisted_2),
      _createVNode(ScrollBar, {
        viewportWidth: programWidth.value,
        viewportHeight: programHeight.value,
        "min-x": _unref(limits)[0][0],
        "max-x": _unref(limits)[1][0],
        modelValue: scrollX.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((scrollX).value = $event))
      }, null, 8, ["viewportWidth", "viewportHeight", "min-x", "max-x", "modelValue"]),
      _createVNode(VScrollBar, {
        viewportWidth: programWidth.value,
        viewportHeight: programHeight.value,
        "min-y": _unref(limits)[0][1],
        "max-y": _unref(limits)[1][1],
        modelValue: scrollY.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((scrollY).value = $event))
      }, null, 8, ["viewportWidth", "viewportHeight", "min-y", "max-y", "modelValue"])
    ])),
    (draggingSnake.value !== null)
      ? (_openBlock(), _createBlock(DraggingSnake, {
          key: 0,
          modelValue: draggingSnake.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((draggingSnake).value = $event))
        }, null, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    (_unref(isRunning))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _hoisted_6,
      _hoisted_7,
      _hoisted_8,
      _createVNode(ChangeLocale),
      _createVNode(ShareTwitter),
      _createVNode(ShowVDNA)
    ])
  ], 544))
}
}

})